/**
 * Email transformation utilities
 * Converts Microsoft Graph API emails to EmailItem format
 */
import type { GraphMessage } from '@/api/types/inbox';
import { type EmailItem } from '@/config/office/types';
import { getInitials } from '@/utils/utils';

/**
 * Default color class for all avatars
 */
export const DEFAULT_AVATAR_COLOR = 'bg-info';

/**
 * Format date/time for display
 * Shows relative labels (Today, Yesterday) for recent dates, then absolute dates for older ones
 * Format: "Today . 10:12 AM", "Yesterday . 10:12 AM", "Dec 11"
 */
export function formatEmailTime(
  dateTimeString: string,
  locale?: string,
  timeZone?: string,
): string {
  if (!dateTimeString) {
    return '';
  }

  const date = new Date(dateTimeString);
  const now = new Date();

  // Normalize both dates to local midnight for accurate day comparison
  // This ensures we compare calendar days in the user's local timezone
  // Both dates use local timezone components to avoid UTC/local mixing issues
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const emailDateLocal = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffMs = todayLocal.getTime() - emailDateLocal.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const tzOpt = timeZone ? { timeZone } : {};
  const timeStr = date.toLocaleTimeString(locale, {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    ...tzOpt,
  });

  if (diffDays === 0) {
    return `Today . ${timeStr}`;
  }

  if (diffDays === 1) {
    return `Yesterday . ${timeStr}`;
  }

  const month = date.toLocaleDateString(locale, { month: 'short', ...tzOpt });
  const day = date.getDate();
  return `${month} ${day}`;
}

/**
 * Extract the line(s) containing the search term from email body
 * Removes everything above the line containing the search term
 */
export function extractLineWithSearchTerm(bodyText: string, searchQuery: string): string {
  if (!bodyText || !searchQuery) {
    return '';
  }

  const searchLower = searchQuery.toLowerCase().trim();
  if (!searchLower) {
    return '';
  }

  // Split by newlines (handle both \n and \r\n)
  const lines = bodyText.split(/\r?\n/);

  // Find the first line containing the search term (case-insensitive)
  const matchingLineIndex = lines.findIndex((line) => line.toLowerCase().includes(searchLower));

  if (matchingLineIndex === -1) {
    // If no exact line match, try to find the search term in the text
    // and extract a snippet around it
    const index = bodyText.toLowerCase().indexOf(searchLower);
    if (index === -1) {
      return '';
    }

    // Extract a reasonable snippet around the search term (about 150 chars)
    const start = Math.max(0, index - 50);
    const end = Math.min(bodyText.length, index + searchQuery.length + 100);
    return bodyText.slice(start, end).trim();
  }

  // Return the matching line, removing any text before it
  // Also include the next line if available for context
  const matchingLine = lines[matchingLineIndex]?.trim() ?? '';
  const nextLine = lines[matchingLineIndex + 1]?.trim();

  if (nextLine && nextLine.length > 0) {
    return `${matchingLine} ${nextLine}`;
  }

  return matchingLine;
}

/**
 * Transform Microsoft Graph API message to EmailItem
 */
export function transformGraphMessageToEmailItem(
  message: GraphMessage,
  searchQuery?: string,
  locale?: string,
  timeZone?: string,
): EmailItem {
  const senderName =
    message.sender?.emailAddress?.name ||
    message.sender?.emailAddress?.address ||
    message?.from?.name ||
    message?.from?.address ||
    'Unknown';

  const senderEmail = message.sender?.emailAddress?.address ?? message?.from?.address ?? '';

  // Use receivedDateTime, fallback to sentDateTime
  const dateTime = message.receivedDateTime ?? message.sentDateTime ?? '';

  // Extract snippet from bodyPreview on frontend when search is active
  // Backend only filters emails, doesn't provide snippets
  let snippet: string | undefined = undefined;
  const trimmedSearch = searchQuery?.trim();
  const searchLower = trimmedSearch?.toLowerCase() ?? '';

  if (trimmedSearch && searchLower) {
    const bodyPreview = message.bodyPreview ?? '';
    const matchesBody = bodyPreview.toLowerCase().includes(searchLower);

    if (matchesBody && bodyPreview) {
      snippet = extractLineWithSearchTerm(bodyPreview, trimmedSearch);
    }
  }

  return {
    id: message.id,
    sender: senderName,
    senderColor: DEFAULT_AVATAR_COLOR,
    senderInitial: getInitials(senderName || senderEmail),
    subject: message.subject ?? '(No Subject)',
    time: formatEmailTime(dateTime, locale, timeZone),
    ...(snippet && { snippet }),
  };
}
